import {
  addDocxBlockAuthoringSchemas
} from "./chunk-N3KCALAB.js";
import {
  getContainerComponents
} from "./chunk-JETULU6Z.js";

// src/schemas/export.ts
import { restructureNameDiscriminatedUnions } from "@json-to-office/shared";
function fixSchemaReferences(schema, rootDefinitionName = "ComponentDefinition") {
  const definitionNames = new Set(
    Object.keys(
      schema.definitions ?? {}
    )
  );
  const definitionRef = (name) => {
    const target = definitionNames.has(name) ? name : rootDefinitionName;
    return definitionNames.has(target) ? `#/definitions/${target}` : void 0;
  };
  const isBareDefinitionRef = (value) => typeof value === "string" && (/^T\d+$/.test(value) || value === rootDefinitionName || definitionNames.has(value));
  function traverse(obj, path = "") {
    if (typeof obj !== "object" || obj === null) return;
    for (const [key, value] of Object.entries(obj)) {
      const currentPath = path ? `${path}.${key}` : key;
      if (value && typeof value === "object") {
        const schemaValue = value;
        if (schemaValue.type === "array" && schemaValue.items && Object.keys(schemaValue.items).length === 0 && definitionNames.has(rootDefinitionName)) {
          schemaValue.items = {
            $ref: `#/definitions/${rootDefinitionName}`
          };
        }
        if (schemaValue.type === "array" && schemaValue.items && typeof schemaValue.items === "object" && "$ref" in schemaValue.items && isBareDefinitionRef(
          schemaValue.items.$ref
        )) {
          const ref = definitionRef(
            schemaValue.items.$ref
          );
          schemaValue.items = ref ? { $ref: ref } : {};
        }
        if (isBareDefinitionRef(schemaValue.$ref)) {
          const ref = definitionRef(schemaValue.$ref);
          if (ref) schemaValue.$ref = ref;
          else delete schemaValue.$ref;
        }
        if (key === "$id" && isBareDefinitionRef(value) && currentPath !== `definitions.${value}.$id`) {
          delete obj[key];
          continue;
        }
        traverse(value, currentPath);
      }
    }
  }
  traverse(schema);
}
function convertToJsonSchema(schema, options = {}) {
  const {
    $schema = "http://json-schema.org/draft-07/schema#",
    $id,
    title,
    description,
    definitions = {}
  } = options;
  const schemaJson = JSON.parse(JSON.stringify(schema));
  const extractedDefinitions = { ...definitions };
  function extractRecursiveSchemas(obj, path = "") {
    if (typeof obj !== "object" || obj === null) return;
    for (const [key, value] of Object.entries(obj)) {
      if (value && typeof value === "object") {
        const schemaValue = value;
        if (schemaValue.$id && typeof schemaValue.$id === "string") {
          const definitionName = schemaValue.$id;
          if (path !== `definitions.${definitionName}`) {
            const { $id: _, ...schemaWithoutId } = schemaValue;
            extractedDefinitions[definitionName] = schemaWithoutId;
            obj[key] = { $ref: `#/definitions/${definitionName}` };
            extractRecursiveSchemas(
              schemaWithoutId,
              `definitions.${definitionName}`
            );
            continue;
          }
        }
        extractRecursiveSchemas(
          value,
          path ? `${path}.${key}` : key
        );
      }
    }
  }
  extractRecursiveSchemas(schemaJson);
  const jsonSchema = {
    $schema
  };
  if ($id) jsonSchema.$id = $id;
  Object.assign(jsonSchema, schemaJson);
  if (title !== void 0) jsonSchema.title = title;
  if (description !== void 0) jsonSchema.description = description;
  if (Object.keys(extractedDefinitions).length > 0) {
    jsonSchema.definitions = extractedDefinitions;
  }
  fixSchemaReferences(jsonSchema);
  addDocxBlockAuthoringSchemas(jsonSchema);
  restructureNameDiscriminatedUnions(jsonSchema);
  return jsonSchema;
}
function createComponentSchema(name, config, componentDefinitionSchema, rootDefinitionName = "ComponentDefinition") {
  const componentStructure = {
    $schema: "http://json-schema.org/draft-07/schema#",
    $id: `${name}.schema.json`,
    title: config.title,
    description: config.description,
    type: "object",
    required: ["name", "props"],
    properties: {
      name: {
        type: "string",
        const: name,
        description: `Component name identifier (must be "${name}")`
      },
      id: {
        type: "string",
        description: "Optional unique identifier for the component"
      },
      props: JSON.parse(JSON.stringify(config.schema))
    }
  };
  const containerNames = getContainerComponents().map((c) => c.name);
  if (containerNames.includes(name)) {
    componentStructure.properties.children = {
      type: "array",
      description: "Children within this container",
      items: {
        $ref: `#/definitions/${rootDefinitionName}`
      }
    };
    if (componentDefinitionSchema) {
      componentStructure.definitions = {
        [rootDefinitionName]: JSON.parse(
          JSON.stringify(componentDefinitionSchema)
        )
      };
    }
  }
  if (config.enhanceForRichContent && name === "table") {
    const componentStructureWithDefs = componentStructure;
    if (!componentStructureWithDefs.definitions) {
      componentStructureWithDefs.definitions = {};
    }
    if (componentDefinitionSchema) {
      componentStructureWithDefs.definitions[rootDefinitionName] = JSON.parse(
        JSON.stringify(componentDefinitionSchema)
      );
    }
    const properties = componentStructure.properties;
    const propsProp = properties.props;
    if (propsProp?.properties && typeof propsProp.properties === "object" && propsProp.properties !== null) {
      const propsProps = propsProp.properties;
      const rowsProp = propsProps.rows;
      if (rowsProp?.items && typeof rowsProp.items === "object" && rowsProp.items !== null) {
        const rowsItems = rowsProp.items;
        const cellSchema = rowsItems.items;
        if (cellSchema?.anyOf && Array.isArray(cellSchema.anyOf)) {
          const componentRef = `#/definitions/${rootDefinitionName}`;
          const hasComponentRef = cellSchema.anyOf.some((item) => {
            const itemObj = item;
            return itemObj.$ref === componentRef;
          });
          if (!hasComponentRef) {
            cellSchema.anyOf.push({
              description: "Rich content cell with component (e.g., image, paragraph)",
              $ref: componentRef
            });
          }
        }
      }
    }
  }
  fixSchemaReferences(componentStructure, rootDefinitionName);
  componentStructure.additionalProperties = false;
  return componentStructure;
}
async function exportSchemaToFile(schema, outputPath, options = {}) {
  const { prettyPrint = true } = options;
  const jsonSchema = prettyPrint ? JSON.stringify(schema, null, 2) : JSON.stringify(schema);
  const fs = await import("fs/promises");
  await fs.writeFile(outputPath, jsonSchema, "utf-8");
}
var COMPONENT_METADATA = {
  report: {
    title: "Report Component",
    description: "Top-level report container component with document-wide settings"
  },
  section: {
    title: "Section Component",
    description: "Section container for organizing document content"
  },
  columns: {
    title: "Columns Component",
    description: "Multi-column layout container"
  },
  heading: {
    title: "Heading Component",
    description: "Heading text with configurable levels and styling"
  },
  paragraph: {
    title: "Paragraph Component",
    description: "Rich paragraph text content with formatting options"
  },
  "text-box": {
    title: "Text Box Component",
    description: "Inline or floating container that groups text and image components with shared positioning"
  },
  image: {
    title: "Image Component",
    description: "Image content with positioning and sizing options"
  },
  statistic: {
    title: "Statistic Component",
    description: "Statistical display with value and label"
  },
  table: {
    title: "Table Component",
    description: "Tabular data display with headers and rows",
    enhanceForRichContent: true
  },
  list: {
    title: "List Component",
    description: "Ordered or unordered list with nested items"
  },
  highcharts: {
    title: "Highcharts Component",
    description: "Charts powered by Highcharts (line, bar, pie, heatmap, etc.) with rich configuration"
  },
  visual: {
    title: "Visual Component",
    description: 'Free-canvas graphic: a rasterized pptx slide, or a native Word drawing group with renderMode "native"'
  }
};
var BASE_SCHEMA_METADATA = {
  alignment: {
    title: "Alignment",
    description: "Text alignment options"
  },
  "base-component": {
    title: "Base Component Props",
    description: "Common props for all components"
  },
  border: {
    title: "Border",
    description: "Border styling configuration"
  },
  spacing: {
    title: "Spacing",
    description: "Spacing configuration for before and after elements"
  },
  margins: {
    title: "Margins",
    description: "Margin configuration for all sides"
  },
  indent: {
    title: "Indent",
    description: "Indentation configuration"
  },
  "line-spacing": {
    title: "Line Spacing",
    description: "Line height and spacing configuration"
  },
  "heading-level": {
    title: "Heading Level",
    description: "Heading level from 1 to 6"
  },
  numbering: {
    title: "Numbering",
    description: "Numbering configuration for ordered lists"
  },
  "justified-alignment": {
    title: "Justified Alignment",
    description: "Justified text alignment options"
  }
};
var THEME_SCHEMA_METADATA = {
  theme: {
    title: "Theme Configuration",
    description: "JSON theme configuration for document styling and appearance"
  }
};

export {
  fixSchemaReferences,
  convertToJsonSchema,
  createComponentSchema,
  exportSchemaToFile,
  COMPONENT_METADATA,
  BASE_SCHEMA_METADATA,
  THEME_SCHEMA_METADATA
};
//# sourceMappingURL=chunk-ITJC36U6.js.map